import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, type JSX } from 'react';
import { Sidebar } from './components/Sidebar';
import { AppHeader } from './components/AppHeader';
import { useAuth } from './lib/auth';
import type { Permission } from './lib/api';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { PlatformFeesPage } from './pages/PlatformFeesPage';
import { FedexBillsPage } from './pages/FedexBillsPage';
import { FedexBillDetailPage } from './pages/FedexBillDetailPage';
import { FedexShipmentDetailPage } from './pages/FedexShipmentDetailPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { ArPage } from './pages/ArPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { PharmacyPayablesPage } from './pages/PharmacyPayablesPage';
import { PayableDetailPage } from './pages/PayableDetailPage';
import { PharmacyCostsPage } from './pages/PharmacyCostsPage';
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { WebhooksPage } from './pages/WebhooksPage';
import { PharmacyBillingPage, PricingGroupPage, PaymentsGroupPage } from './pages/tabGroups';
import { NotFoundPage } from './pages/NotFoundPage';
import { PayPage } from './pages/PayPage';
import { AddCardPage } from './pages/AddCardPage';
import { SetPasswordPage } from './pages/SetPasswordPage';

/** Where to send a user who lacks permission for the page they hit. */
/**
 * Pages that used to have their own sidebar entry and are now tabs. Their old
 * paths still resolve — bookmarks and links in old emails keep working — by
 * redirecting to the tab that replaced them.
 */
export const RETIRED_ROUTES: readonly (readonly [from: string, to: string])[] = [
  ['/pharmacy-volume', '/pharmacy?tab=volume'],
  ['/shipping', '/pharmacy?tab=shipping'],
  ['/price-coverage', '/pricing?tab=coverage'],
  ['/payment-methods', '/payments?tab=methods'],
];

export const landingPath = (can: (p: Permission) => boolean): string =>
  can('dashboard:read') ? '/' : '/invoices';

const Protected = ({ perm, children }: { perm?: Permission; children: JSX.Element }) => {
  const { can } = useAuth();
  if (perm && !can(perm)) return <Navigate to={landingPath(can)} replace />;
  return children;
};

export const App = () => (
  <Routes>
    {/* Public hosted pages — rendered before the auth gate. */}
    <Route path="/pay/:token" element={<PayPage />} />
    <Route path="/add-card/:token" element={<AddCardPage />} />
    <Route path="/set-password/:token" element={<SetPasswordPage />} />
    <Route path="*" element={<AuthedApp />} />
  </Routes>
);

const AuthedApp = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setNavOpen(false), [location.pathname]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--p-muted-fg)',
          fontFamily: 'var(--font-product)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <div className="app-shell">
      <Sidebar open={navOpen} onNavigate={() => setNavOpen(false)} />
      {navOpen && <div className="app-scrim" onClick={() => setNavOpen(false)} />}
      <div className="app-main">
        <AppHeader onMenuClick={() => setNavOpen(true)} />
        <div className="app-content">
          <Routes>
            <Route path="/" element={<Protected perm="dashboard:read"><DashboardPage /></Protected>} />
            <Route path="/platform" element={<Protected perm="dashboard:read"><PlatformFeesPage /></Protected>} />
            <Route path="/pharmacy" element={<Protected perm="dashboard:read"><PharmacyBillingPage /></Protected>} />
            {RETIRED_ROUTES.map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            <Route path="/fedex" element={<Protected perm="dashboard:read"><FedexBillsPage /></Protected>} />
            <Route path="/fedex/:id" element={<Protected perm="dashboard:read"><FedexBillDetailPage /></Protected>} />
            <Route path="/fedex/:invoiceId/shipments/:chargeId" element={<Protected perm="dashboard:read"><FedexShipmentDetailPage /></Protected>} />
            <Route path="/payables" element={<Protected perm="dashboard:read"><PharmacyPayablesPage /></Protected>} />
            <Route path="/payables/:id" element={<Protected perm="dashboard:read"><PayableDetailPage /></Protected>} />
            <Route path="/pharmacy-costs" element={<Protected perm="dashboard:read"><PharmacyCostsPage /></Protected>} />
            <Route path="/invoices" element={<Protected perm="invoices:read"><InvoicesPage /></Protected>} />
            <Route path="/invoices/:id" element={<Protected perm="invoices:read"><InvoiceDetailPage /></Protected>} />
            <Route path="/ar" element={<Protected perm="invoices:read"><ArPage /></Protected>} />
            <Route path="/payments" element={<Protected perm="payments:read"><PaymentsGroupPage /></Protected>} />
            <Route path="/clients" element={<Protected perm="clients:read"><ClientsPage /></Protected>} />
            <Route path="/clients/:id" element={<Protected perm="clients:read"><ClientDetailPage /></Protected>} />
            <Route path="/pricing" element={<Protected perm="pricing:read"><PricingGroupPage /></Protected>} />
            <Route path="/users" element={<Protected perm="users:read"><UsersPage /></Protected>} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/webhooks" element={<Protected perm="dashboard:read"><WebhooksPage /></Protected>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};
